import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { API } from "@/App";

const AskIvana = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
      if (messages.length === 0) {
        setMessages([
          {
            role: "assistant",
            content:
              "Hi, I'm Ivana. Ask me anything about perimenopause, peptides, nutrition, sleep, or your next step. I'm here to help.",
          },
        ]);
      }
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isStreaming) return;

    setInput("");
    const newUserMsg = { role: "user", content: text };
    const history = messages.filter((m) => m.role !== "system");
    setMessages((prev) => [...prev, newUserMsg, { role: "assistant", content: "" }]);
    setIsStreaming(true);

    try {
      const response = await fetch(`${API}/chat/ask-ivana`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: text,
          session_id: sessionId,
          history: history.map((m) => ({ role: m.role, content: m.content })),
        }),
      });

      if (!response.ok) throw new Error("Chat error");
      const data = await response.json();

      if (data.session_id && !sessionId) setSessionId(data.session_id);
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: "assistant",
          content: data.reply || "I couldn't come up with an answer just now. Try asking a different way?",
        };
        return updated;
      });
    } catch (error) {
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: "assistant",
          content: "I'm having trouble responding right now. Please try again in a moment.",
        };
        return updated;
      });
    } finally {
      setIsStreaming(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-3 pl-4 pr-5 py-3 rounded-full bg-[#1F2818] text-white shadow-2xl hover:bg-[#2F3B26] transition-all hover:-translate-y-1 group"
          data-testid="ask-ivana-trigger"
        >
          <div className="w-8 h-8 rounded-full bg-[#B89660] flex items-center justify-center">
            <Sparkles className="w-4 h-4" strokeWidth={2} />
          </div>
          <span className="font-medium text-sm">Ask Ivana</span>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] max-w-[400px] h-[600px] max-h-[calc(100vh-4rem)] rounded-3xl bg-[#F6F4EF] shadow-2xl flex flex-col overflow-hidden animate-scale-in border border-[#E3DAC9]"
          data-testid="ask-ivana-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-[#E3DAC9] bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1F2818] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#B89660]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-heading text-lg text-[#1F2818] leading-none">
                  Ask Ivana
                </p>
                <p className="text-xs text-[#4A5543] tracking-wider">
                  AI trained on my framework
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-[#EAE2D0] rounded-full transition-colors"
              data-testid="ask-ivana-close"
            >
              <X className="w-4 h-4 text-[#4A5543]" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`message-${msg.role}-${i}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "bg-[#B89660] text-white rounded-br-sm"
                      : "bg-white text-[#1F2818] rounded-bl-sm border border-[#E3DAC9]"
                  }`}
                >
                  {msg.content || (isStreaming && i === messages.length - 1 ? (
                    <Loader2 className="w-4 h-4 animate-spin text-[#B89660]" />
                  ) : "")}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-[#E3DAC9] bg-white">
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about your reset type, peptides, sleep..."
                rows={1}
                className="flex-1 resize-none rounded-2xl border border-[#E3DAC9] px-4 py-3 text-sm focus:outline-none focus:border-[#B89660] focus:ring-2 focus:ring-[#B89660]/15 max-h-24"
                data-testid="ask-ivana-input"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isStreaming}
                className="p-3 rounded-full bg-[#B89660] text-white disabled:opacity-40 hover:bg-[#C7A874] transition-colors"
                data-testid="ask-ivana-send"
              >
                {isStreaming ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
            <p className="text-[10px] text-[#4A5543]/50 mt-2 text-center tracking-wide">
              AI responses. Not medical advice.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AskIvana;
